/* eslint-disable import/prefer-default-export */
import _ from 'lodash';
import {
  colors as colorsMaterial,
  createTheme,
  responsiveFontSizes,
} from '@mui/material';
import * as locales from '@mui/material/locale';
import { useTranslation } from 'react-i18next';
import typography from './typography';
import { softShadows, strongShadows } from './shadows';
import { THEMES } from '../constants';

export const themePrimaryColor = '#2f4a9c';
export const themeSecondaryColor = '#7a7b74';
export const themeTertiaryColor = '#159191';

const colors = {
  ...colorsMaterial,
  custom: {
    grey: {
      1: '#F6F7F7',
    },
  },
};

const baseConfig = {
  direction: 'ltr',
  typography,
  overrides: {
    MuiLinearProgress: {
      root: {
        borderRadius: 3,
        overflow: 'hidden',
      },
    },
    MuiListItemIcon: {
      root: {
        minWidth: 32,
      },
    },
    MuiChip: {
      root: {
        backgroundColor: 'rgba(0,0,0,0.075)',
      },
    },
  },
};

const aceleraComponents = {
  MuiButton: {
    styleOverrides: {
      root: {
        borderRadius: 8,
        textTransform: 'none',
        fontWeight: 600,
        letterSpacing: 0,
        boxShadow: 'none',
      },
      contained: {
        boxShadow: 'none',
        '&:hover': { boxShadow: '0 3px 10px rgba(47, 74, 156, 0.15)' },
      },
      outlined: { borderColor: '#dbe2ed' },
    },
  },
  MuiCard: {
    styleOverrides: {
      root: {
        borderRadius: 12,
        border: '1px solid #e2e8f0',
        backgroundImage: 'none',
        boxShadow: '0 2px 10px rgba(15, 23, 42, 0.045)',
      },
    },
  },
  MuiCardHeader: {
    styleOverrides: {
      root: { padding: '20px 22px 12px' },
    },
  },
  MuiCardContent: {
    styleOverrides: {
      root: { padding: 20, '&:last-child': { paddingBottom: 20 } },
    },
  },
  MuiOutlinedInput: {
    styleOverrides: {
      root: {
        borderRadius: 8,
        backgroundColor: '#ffffff',
        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#9aaad0' },
      },
      notchedOutline: { borderColor: '#dbe2ed' },
    },
  },
  MuiTableContainer: {
    styleOverrides: {
      root: { borderRadius: 10 },
    },
  },
  MuiTableHead: {
    styleOverrides: {
      root: { backgroundColor: '#f1f5f9' },
    },
  },
  MuiTableCell: {
    styleOverrides: {
      root: { borderBottom: '1px solid #e9edf4' },
      head: {
        color: '#475569',
        fontWeight: 700,
        fontSize: '0.75rem',
        letterSpacing: '0.015em',
      },
    },
  },
  MuiTab: {
    styleOverrides: {
      root: { textTransform: 'none', fontWeight: 600, minHeight: 52 },
    },
  },
  MuiTabs: {
    styleOverrides: {
      indicator: { height: 3, borderRadius: 3 },
    },
  },
  MuiChip: {
    styleOverrides: {
      root: { borderRadius: 6, fontWeight: 600 },
    },
  },
  MuiDialog: {
    styleOverrides: {
      paper: { borderRadius: 12 },
    },
  },
};

const themeConfigs = [
  {
    name: THEMES.LIGHT,
    overrides: {
      MuiInputBase: {
        input: {
          '&::placeholder': {
            opacity: 1,
            color: colors.blueGrey[600],
          },
        },
      },
    },
    palette: {
      mode: 'light',
      action: {
        active: colors.blueGrey[600],
      },
      background: {
        default: colors.common.white,
        dark: '#f4f6f8',
        paper: colors.common.white,
      },
      primary: {
        main: colors.indigo[600],
      },
      secondary: {
        main: '#5850EC',
      },
      error: {
        main: '#7f0000',
      },
      text: {
        primary: colors.blueGrey[900],
        secondary: colors.blueGrey[600],
      },
      button: {
        highlight: colors.custom.grey[1],
      },
    },
    shadows: softShadows,
  },
  {
    name: THEMES.AZUL,
    overrides: {
      MuiInputBase: {
        input: {
          '&::placeholder': {
            opacity: 1,
            color: colors.blueGrey[600],
          },
        },
      },
    },
    palette: {
      mode: 'light',
      action: {
        active: colors.blueGrey[600],
      },
      background: {
        default: colors.common.white,
        dark: '#f4f6f8',
        paper: colors.common.white,
      },
      primary: {
        main: '#00408e',
      },
      secondary: {
        main: '#00408e',
      },
      error: {
        main: '#7f0000',
      },
      text: {
        primary: colors.blueGrey[900],
        secondary: colors.blueGrey[600],
      },
      button: {
        highlight: colors.custom.grey[1],
      },
    },
    shadows: softShadows,
  },
  {
    name: THEMES.ONE_DARK,
    palette: {
      mode: 'dark',
      action: {
        active: 'rgba(255, 255, 255, 0.54)',
        hover: 'rgba(255, 255, 255, 0.04)',
        selected: 'rgba(255, 255, 255, 0.08)',
        disabled: 'rgba(255, 255, 255, 0.26)',
        disabledBackground: 'rgba(255, 255, 255, 0.12)',
        focus: 'rgba(255, 255, 255, 0.12)',
      },
      background: {
        default: '#282C34',
        dark: '#1c2025',
        paper: '#282C34',
      },
      primary: {
        main: themePrimaryColor,
      },
      secondary: {
        main: themePrimaryColor,
      },
      error: {
        main: '#7f0000',
      },
      text: {
        primary: '#e6e5e8',
        secondary: '#adb0bb',
      },
    },
    shadows: strongShadows,
  },
  {
    name: THEMES.UNICORN,
    palette: {
      mode: 'dark',
      action: {
        active: 'rgba(255, 255, 255, 0.54)',
        hover: 'rgba(255, 255, 255, 0.04)',
        selected: 'rgba(255, 255, 255, 0.08)',
        disabled: 'rgba(255, 255, 255, 0.26)',
        disabledBackground: 'rgba(255, 255, 255, 0.12)',
        focus: 'rgba(255, 255, 255, 0.12)',
      },
      background: {
        default: '#2a2d3d',
        dark: '#222431',
        paper: '#2a2d3d',
      },
      primary: {
        main: '#a67dff',
      },
      secondary: {
        main: '#a67dff',
      },
      error: {
        main: '#7f0000',
      },
      text: {
        primary: '#f6f5f8',
        secondary: '#9699a4',
      },
    },
    shadows: strongShadows,
  },
  {
    name: THEMES.MICREDI,
    palette: {
      mode: 'dark',
      action: {
        active: 'rgba(255, 255, 255, 0.54)',
        hover: 'rgba(255, 255, 255, 0.04)',
        selected: 'rgba(255, 255, 255, 0.08)',
        disabled: 'rgba(255, 255, 255, 0.26)',
        disabledBackground: 'rgba(255, 255, 255, 0.12)',
        focus: 'rgba(255, 255, 255, 0.12)',
      },
      background: {
        default: '#FFFFFF',
        dark: '#FFFFFF',
        paper: '#FFFFFF',
      },
      primary: {
        main: '#4CE079',
      },
      secondary: {
        main: '#30894B',
      },
      error: {
        main: '#7f0000',
      },
      text: {
        primary: '#2A2D3D',
        secondary: '#2A2D3D',
      },
    },
    shadows: strongShadows,
  },
  {
    name: THEMES.ACELERA,
    components: aceleraComponents,
    overrides: {
      MuiInputBase: {
        input: {
          '&::placeholder': {
            opacity: 1,
            color: colors.blueGrey[600],
          },
        },
      },
    },
    palette: {
      mode: 'light',
      action: {
        active: colors.blueGrey[600],
      },
      background: {
        default: '#f8fafc',
        dark: '#f1f5f9',
        paper: colors.common.white,
      },
      primary: {
        main: themePrimaryColor,
      },
      secondary: {
        main: themePrimaryColor,
        logo: '#fff',
      },
      error: {
        main: '#7f0000',
      },
      text: {
        primary: colors.blueGrey[900],
        secondary: colors.blueGrey[600],
      },
      button: {
        highlight: colors.custom.grey[1],
      },
    },
    shadows: softShadows,
  },
];

export function createMuiTheme(settings = {}) {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { i18n } = useTranslation();
  let themeConfig = themeConfigs.find((theme) => theme.name === settings.theme);
  if (!themeConfig) {
    [themeConfig] = themeConfigs;
  }

  let theme = createTheme(
    _.merge({}, baseConfig, themeConfig, { direction: settings.direction }),
    Object.entries(locales).find((locale) => locale[0].startsWith(i18n.sublanguage)),
  );

  if (settings.responsiveFontSizes) {
    theme = responsiveFontSizes(theme);
  }

  return theme;
}
